/**
 * Overland位置データ用Zustandストア
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { create } from 'zustand'

import { getOverlandLocations, getOverlandStats, getOverlandVisits } from '@/api/services/overland'
import type { OverlandLocation, OverlandStats, OverlandVisit } from '@/api/types/overland'
import { generateMovementSessions, type MovementSessionWithVisits } from '@/lib/movement-session'

type OverlandState = {
  // 統計情報
  stats: OverlandStats | null
  isStatsLoading: boolean

  // 位置データ
  locations: OverlandLocation[]
  isLocationsLoading: boolean

  // 訪問データ
  visits: OverlandVisit[]
  isVisitsLoading: boolean

  // 削除された visit ID（外部キャッシュ無効化用、消費後に clearRemovedVisitIds で空にする）
  removedVisitIds: number[]

  // upsertVisit/removeVisit のたびに増える。日付切替などの読み込みと区別して、編集に追従したい処理が購読する
  visitMutationVersion: number

  // 移動セッション（位置データから生成）
  movementSessions: MovementSessionWithVisits[]

  // エラー
  error: string | null

  // アクション
  fetchStats: () => Promise<void>
  fetchLocations: (date?: string, limit?: number, options?: { silent?: boolean }) => Promise<void>
  fetchVisits: (date?: string, limit?: number, options?: { silent?: boolean }) => Promise<void>
  fetchAll: (date?: string) => Promise<void>
  /** 単一 visit を追加 or 更新（部分更新用） */
  upsertVisit: (visit: OverlandVisit) => void
  /** 単一 visit をローカル配列から削除 */
  removeVisit: (visitId: number) => void
  /** 外部キャッシュが消費した removedVisitIds をクリア（指定 ID を除外、無指定なら全消去） */
  clearRemovedVisitIds: (ids?: number[]) => void
  clearError: () => void
  reset: () => void
}

const initialState = {
  stats: null,
  isStatsLoading: false,
  locations: [],
  isLocationsLoading: false,
  visits: [],
  isVisitsLoading: false,
  removedVisitIds: [],
  visitMutationVersion: 0,
  movementSessions: [],
  error: null,
}

export const useOverlandStore = create<OverlandState>((set, get) => ({
  ...initialState,

  fetchStats: async () => {
    set({ isStatsLoading: true, error: null })
    try {
      const stats = await getOverlandStats()
      set({ stats })
    } catch (error) {
      const message = error instanceof Error ? error.message : '統計情報の取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isStatsLoading: false })
    }
  },

  fetchLocations: async (date?: string, limit?: number, options?: { silent?: boolean }) => {
    if (!options?.silent) set({ isLocationsLoading: true, error: null })
    try {
      const locations = await getOverlandLocations(date, limit)
      const { visits } = get()
      // visitsを考慮して移動セッションを生成
      const movementSessions = generateMovementSessions(locations, visits)
      set({ locations, movementSessions })
    } catch (error) {
      const message = error instanceof Error ? error.message : '位置データの取得に失敗しました'
      set({ error: message })
    } finally {
      if (!options?.silent) set({ isLocationsLoading: false })
    }
  },

  fetchVisits: async (date?: string, limit = 100, options?: { silent?: boolean }) => {
    if (!options?.silent) set({ isVisitsLoading: true, error: null })
    try {
      const visits = await getOverlandVisits(date, limit)
      const { locations } = get()
      // locations が空でも generateMovementSessions は [] を返すため、常にセットして整合性を保つ
      set({ visits, movementSessions: generateMovementSessions(locations, visits) })
    } catch (error) {
      const message = error instanceof Error ? error.message : '訪問データの取得に失敗しました'
      set({ error: message })
    } finally {
      if (!options?.silent) set({ isVisitsLoading: false })
    }
  },

  fetchAll: async (date?: string) => {
    set({ isLocationsLoading: true, isVisitsLoading: true, error: null })
    try {
      // 並列取得し、両方の結果を一括でmovementSessionsに反映
      const [locations, visits] = await Promise.all([getOverlandLocations(date), getOverlandVisits(date)])
      const movementSessions = generateMovementSessions(locations, visits)
      set({ locations, visits, movementSessions })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'データの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isLocationsLoading: false, isVisitsLoading: false })
    }
  },

  upsertVisit: (visit: OverlandVisit) => {
    const { visits, locations, visitMutationVersion } = get()
    const existingIndex = visits.findIndex((v) => v.id === visit.id)
    const nextVisits = existingIndex >= 0 ? visits.map((v, i) => (i === existingIndex ? visit : v)) : [...visits, visit]
    set({
      visits: nextVisits,
      movementSessions: generateMovementSessions(locations, nextVisits),
      visitMutationVersion: visitMutationVersion + 1,
    })
  },

  removeVisit: (visitId: number) => {
    const { visits, locations, removedVisitIds, visitMutationVersion } = get()
    const nextVisits = visits.filter((v) => v.id !== visitId)
    const nextRemoved = removedVisitIds.includes(visitId) ? removedVisitIds : [...removedVisitIds, visitId]
    if (nextVisits.length === visits.length) {
      // store には存在しなかったが、外部キャッシュ側で持っている可能性があるため ID は通知する
      set({ removedVisitIds: nextRemoved, visitMutationVersion: visitMutationVersion + 1 })
      return
    }
    set({
      visits: nextVisits,
      movementSessions: generateMovementSessions(locations, nextVisits),
      removedVisitIds: nextRemoved,
      visitMutationVersion: visitMutationVersion + 1,
    })
  },

  clearRemovedVisitIds: (ids?: number[]) => {
    if (!ids || ids.length === 0) {
      set({ removedVisitIds: [] })
      return
    }
    const consumed = new Set(ids)
    set({ removedVisitIds: get().removedVisitIds.filter((id) => !consumed.has(id)) })
  },

  clearError: () => set({ error: null }),
  reset: () => set(initialState),
}))
